import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { BrandSitesV2Theme } from '../common/GlobalStyle';
import { Variants, motion, useAnimation, useInView } from 'framer-motion';

export default function CommerceSection() {
  const cardGridRef = useRef<HTMLDivElement>(null);
  const cardInView = useInView(cardGridRef);
  const controls = useAnimation();

  const variants: Variants = {
    initial: { y: 48, opacity: 0, transition: { delay: 0, duration: 0.2 } },
    animate: (delay) => ({ y: 0, opacity: 1, transition: { delay, duration: 0.5, ease: 'easeOut' } }),
  };

  useEffect(() => {
    if (cardInView) controls.start('animate');
    else controls.start('initial');
  }, [cardInView]);

  return (
    <Container>
      <Wrapper>
        <Top>
          <TitleWrapper>
            <FakeTitle>일상에 편리함을 더할 커머스 플랫폼</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
          </TitleWrapper>
          <TopContent>
            <Description>
              약 230만건의 서비스 데이터를 기반으로 꾸린 상품 라인업을 통해 고객에게 일상에 편리함을 더하는 경험을
              제공합니다.
            </Description>
          </TopContent>
        </Top>
        <Bottom>
          <DesktopTabletImageWrapper>
            <picture>
              <source
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/webp/commerce_tablet.webp"
                type="image/webp"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/webp/commerce_web.webp"
                type="image/webp"
              />

              <source
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_tablet.png"
                type="image/png"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_web.png"
                type="image/png"
              />
              <img
                src="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_web.png"
                alt=""
                draggable={false}
              />
            </picture>
          </DesktopTabletImageWrapper>
          <MobileImageGrid initial="initial" animate={controls} ref={cardGridRef}>
            <Mobile1 custom={0.1} variants={variants}>
              <picture>
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/webp/commerce_mobile1.webp"
                  type="image/webp"
                />
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile1.png"
                  type="image/png"
                />
                <MobileImg
                  src="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile1.png"
                  alt=""
                  style={{ width: `${(200 / 327) * 100}%` }}
                  draggable={false}
                />
              </picture>
            </Mobile1>
            <Mobile2 custom={0.2} variants={variants}>
              <picture>
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/webp/commerce_mobile2.webp"
                  type="image/webp"
                />
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile2.png"
                  type="image/png"
                />
                <MobileImg
                  src="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile2.png"
                  alt=""
                  style={{ width: `${(115 / 157) * 100}%` }}
                  draggable={false}
                />
              </picture>
            </Mobile2>
            <Mobile3 custom={0.3} variants={variants}>
              <picture>
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/webp/commerce_mobile3.webp"
                  type="image/webp"
                />
                <source
                  srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile3.png"
                  type="image/png"
                />
                <MobileImg
                  src="https://cleanhome-web.cleaninglab.co.kr/brandsite/store/commerce_mobile3.png"
                  alt=""
                  style={{ width: `${(115 / 157) * 100}%` }}
                  draggable={false}
                />
              </picture>
            </Mobile3>
          </MobileImageGrid>
        </Bottom>
      </Wrapper>
    </Container>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
  padding: 0 24px;
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  padding-top: 120px;
  padding-bottom: 120px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 80px;
    padding-bottom: 107px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    flex-direction: column;
    padding-top: 81px;
    padding-bottom: 40px;
  }
`;

const TitleWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 24px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const TopContent = styled.div``;

const Description = styled.h2`
  /* word-break: keep-all; */
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  width: 502px;
  letter-spacing: -1px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;

    width: 327px;
    letter-spacing: normal;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: auto;
    font-weight: 400;
  }
`;

const Bottom = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-bottom: 40px;
  }
`;

const DesktopTabletImageWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 311px;
    overflow: hidden;

    img {
      object-fit: contain;
    }
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }

  img {
    width: 100%;
    user-select: none;
  }
`;

const MobileImageGrid = styled(motion.div)`
  grid-template:
    'mobile1 mobile1' auto
    'mobile2 mobile3' auto
    / 1fr 1fr;
  row-gap: 14px;
  column-gap: 13px;

  display: none;
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: grid;
  }
`;

const Mobile1 = styled(motion.div)`
  grid-area: mobile1;
  position: relative;
  background: rgba(248, 248, 248, 1);

  &::before {
    display: block;
    content: '';
    padding-bottom: 100%;
  }
`;

const Mobile2 = styled(motion.div)`
  grid-area: mobile2;
  position: relative;
  background: rgba(248, 248, 248, 1);

  &::before {
    display: block;
    content: '';
    padding-bottom: ${(193 / 157) * 100}%;
  }
`;

const Mobile3 = styled(motion.div)`
  grid-area: mobile3;
  position: relative;
  background: rgba(248, 248, 248, 1);

  &::before {
    display: block;
    content: '';
    padding-bottom: ${(193 / 157) * 100}%;
  }
`;

const MobileImg = styled.img`
  display: block;
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
`;

const TitleDesktopSvg = (
  <svg width="352" height="105" viewBox="0 0 352 105" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.312 11.884C0.312 6.088 4.958 1.81 11.444 1.81C18.068 1.81 22.76 6.088 22.76 11.884C22.76 17.496 18.068 21.912 11.444 21.912C4.958 21.912 0.312 17.496 0.312 11.884ZM5.418 11.838C5.418 15.104 7.902 17.496 11.444 17.496C15.032 17.496 17.654 15.104 17.654 11.838C17.654 8.572 15.032 6.134 11.444 6.134C7.902 6.134 5.418 8.572 5.418 11.838ZM35.134 22.418H30.028V0.659999H35.134V22.418ZM35.778 44.452H9.788V32.492H30.028V29.088H9.788V24.672H35.134V36.816H14.848V40.082H35.778V44.452ZM64.4784 19.198L61.6724 23.108C57.7164 21.544 53.7144 17.91 52.0124 14.046C50.4484 17.956 46.8144 22.326 42.1684 24.35L39.0404 20.624C44.6984 17.772 49.2064 12.62 49.2064 5.72V1.764H54.5884V5.628C54.5884 12.068 59.4644 17.496 64.4784 19.198ZM68.6644 34.7C68.6644 31.434 65.9964 29.318 62.2704 29.318C58.5904 29.318 55.9224 31.434 55.9224 34.7C55.9224 37.966 58.5904 40.128 62.2704 40.128C65.9964 40.128 68.6644 37.966 68.6644 34.7ZM73.7704 34.7C73.7704 40.588 68.9864 44.498 62.2704 44.498C55.6004 44.498 50.8624 40.588 50.8624 34.7C50.8624 29.226 55.6004 24.948 62.2704 24.948C68.9864 24.948 73.7704 29.226 73.7704 34.7ZM80.5324 14.874H73.5864V24.58H68.4804V0.659999H73.5864V10.504H80.5324V14.874ZM86.5548 15.196C86.5548 19.474 88.9008 21.958 91.8908 21.958C95.0188 21.958 97.2728 19.474 97.2728 15.196C97.2728 10.964 95.0188 8.388 91.8908 8.388C88.9008 8.388 86.5548 10.964 86.5548 15.196ZM113.373 39.116V0.751998H118.387V39.116H113.373ZM81.6788 15.242C81.6788 8.388 85.9568 3.88 91.8908 3.88C96.4908 3.88 99.8948 6.41 101.459 10.596C101.689 11.286 101.873 11.976 102.011 12.712H105.185V1.304H110.061V37.644H105.185V17.082H102.149C102.011 18.094 101.781 19.06 101.459 19.934C99.8948 24.028 96.5368 26.604 91.8908 26.604C85.9568 26.604 81.6788 22.096 81.6788 15.242ZM158.232 7.606H136.336V3.236H158.232V7.606ZM171.94 42.934H146.502V28.904H151.608V38.518H171.94V42.934ZM171.25 30.882H166.144V19.75H159.52V15.334H166.144V11.792H159.52V7.422H166.144V0.705998H171.25V30.882ZM159.796 20.578L160.164 24.764C153.264 25.73 143.512 26.098 135.646 25.868L135.278 21.498C136.888 21.544 138.682 21.544 140.568 21.544L139.648 10.044L144.57 9.676L145.214 21.544C146.778 21.544 148.25 21.498 149.722 21.452L150.504 9.676L155.334 9.998L154.322 21.268C154.368 21.222 154.46 21.222 154.506 21.222C156.53 21.084 158.278 20.9 159.796 20.578ZM203.216 23.43L203.768 27.708C197.604 29.134 186.656 29.41 179.02 29.042V13.402H193.326V7.284H178.928V2.868H198.432V17.726H184.126V24.764C189.002 24.994 197.926 24.534 203.216 23.43ZM207.08 39.07V0.751998H212.14V39.07H207.08ZM242.221 11.424H217.749V7.146H242.221V11.424ZM236.103 4.846H224.373V0.521998H236.103V4.846ZM234.907 20.44C234.907 18.37 233.113 16.898 230.353 16.898C227.547 16.898 225.753 18.37 225.753 20.44C225.753 22.418 227.547 23.982 230.353 23.982C233.113 23.982 234.907 22.418 234.907 20.44ZM239.829 20.44C239.829 25.04 235.551 27.938 230.353 27.938C224.971 27.938 220.647 25.04 220.647 20.44C220.647 15.61 224.971 12.85 230.353 12.85C235.689 12.85 239.829 15.61 239.829 20.44ZM246.039 40.036V34.378H231.549V40.036H246.039ZM251.099 44.406H226.489V29.962H251.099V44.406ZM258.045 16.392H251.099V27.662H246.039V0.659999H251.099V12.022H258.045V16.392ZM285.043 9.446C285.043 6.778 282.283 4.708 278.511 4.708C274.601 4.708 271.979 6.686 271.979 9.446C271.979 12.344 274.509 14.23 278.511 14.23C282.283 14.23 285.043 12.344 285.043 9.446ZM290.241 9.446C290.241 15.38 284.353 18.462 278.557 18.462C272.715 18.462 266.735 15.472 266.735 9.446C266.735 3.834 272.301 0.383997 278.511 0.383997C284.997 0.383997 290.241 3.926 290.241 9.446ZM292.495 44.636H265.677V33.504H286.331V30.882H265.677V26.604H291.345V37.69H270.645V40.358H292.495V44.636ZM297.877 24.074H259.145V19.704H297.877V24.074ZM1.186 88.214V63.144H21.15V67.514H6.292V83.614C11.168 83.844 17.332 83.476 22.714 82.326L23.22 86.834C16.688 88.168 9.696 88.536 1.186 88.214ZM35.134 99.07H30.028V76.944H19.264V72.62H30.028V60.752H35.134V99.07ZM64.6624 70.78H40.1904V66.686H64.6624V70.78ZM58.5444 64.846H46.8144V60.706H58.5444V64.846ZM57.3484 78.922C57.3484 77.312 55.5544 76.07 52.7944 76.07C49.9884 76.07 48.1944 77.312 48.1944 78.922C48.1944 80.532 49.9884 81.774 52.7944 81.774C55.5544 81.774 57.3484 80.532 57.3484 78.922ZM62.2704 78.876C62.2704 83.016 57.9924 85.592 52.7944 85.592C47.4124 85.592 43.0884 83.016 43.0884 78.876C43.0884 74.92 47.4124 72.206 52.7944 72.206C58.1304 72.206 62.2704 74.92 62.2704 78.876ZM74.3224 104.59H49.2984V93.78H68.4804V91.296H49.2984V87.156H73.5404V97.828H54.4044V100.45H74.3224V104.59ZM80.4864 75.886H73.5404V84.994H68.4804V60.66H73.5404V71.516H80.4864V75.886ZM129.853 99.07H124.701V77.588H117.157V73.172H124.701V60.752H129.853V99.07ZM97.0554 63.006H116.145C116.283 77.772 108.923 86.006 96.8254 90.652L94.2034 86.604C99.4934 84.718 103.449 82.326 106.209 79.198C106.991 78.324 107.635 77.404 108.233 76.438L96.0894 77.266L95.1694 72.804L110.027 72.206C110.441 70.734 110.763 69.124 110.901 67.422H97.0554V63.006ZM151.608 83.016V67.514H141.948V83.016H151.608ZM156.76 87.34H136.796V63.144H156.76V87.34ZM171.296 99.07H166.144V77.036H159.014V72.712H166.144V60.752H171.296V99.07ZM192.774 62.5H198.616V64.846C198.616 72.39 205.056 77.266 212.232 79.244L209.886 83.66C203.124 81.636 198.11 77.772 195.718 72.804C193.51 77.726 188.082 81.774 181.596 83.752L179.204 79.336C186.472 77.358 192.774 72.39 192.774 64.846V62.5ZM215.084 94.47H176.352V90.192H215.084V94.47ZM262.933 61.212V65.582H235.195V61.212H262.933ZM268.453 84.488H229.721V80.118H268.453V84.488ZM263.439 73.54V77.726H234.735V73.54H240.485L239.749 67.698L244.993 67.284L245.545 73.54H252.629L253.319 67.284L258.655 67.698L257.643 73.54H263.439ZM263.071 104.452H235.931V93.642H256.953V91.296H235.931V87.018H261.921V97.782H240.899V100.22H263.071V104.452ZM296.648 87.57V89.042C296.648 95.16 303.134 99.3 309.39 100.266L307.136 104.452C301.064 102.98 296.096 99.898 293.934 95.62C291.68 99.99 287.034 103.026 280.64 104.636L278.616 100.45C285.332 99.254 291.128 95.344 291.128 89.042V87.57H296.648ZM291.082 79.244L291.588 83.614C286.344 84.58 278.708 84.856 272.084 84.488V71.332H283.584V67.008H272.084V62.684H288.69V75.564H277.19V80.256C281.422 80.486 287.264 80.072 291.082 79.244ZM307.918 88.398H302.858V74.874H298.534V85.04H293.566V61.258H298.534V70.504H302.858V60.66H307.918V88.398ZM340.022 100.128V94.148H323.738V100.128H340.022ZM345.726 61.672V65.95H317.988V61.672H345.726ZM346.232 74.966V79.152H334.41V82.694H351.246V87.018H312.514V82.694H329.396V79.152H317.528V74.966H323.324L322.542 68.112L327.832 67.652L328.384 74.966H335.376L336.112 67.652L341.448 68.112L340.436 74.966H346.232ZM345.082 104.406H318.678V89.778H345.082V104.406Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="212" height="65" viewBox="0 0 212 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.944 7.258C0.944 3.856 3.671 1.345 7.478 1.345C11.366 1.345 14.12 3.856 14.12 7.258C14.12 10.552 11.366 13.144 7.478 13.144C3.671 13.144 0.944 10.552 0.944 7.258ZM3.941 7.231C3.941 9.148 5.399 10.552 7.478 10.552C9.584 10.552 11.123 9.148 11.123 7.231C11.123 5.314 9.584 3.883 7.478 3.883C5.399 3.883 3.941 5.314 3.941 7.231ZM21.383 13.441H18.386V0.669999H21.383V13.441ZM21.761 26.374H6.506V19.354H18.386V17.356H6.506V14.764H21.383V21.892H9.476V23.809H21.761V26.374ZM39.1469 11.551L37.4999 13.846C35.1779 12.928 32.8289 10.795 31.8299 8.527C30.9119 10.822 28.7789 13.387 26.0519 14.575L24.2159 12.388C27.5369 10.714 30.1829 7.69 30.1829 3.64V1.318H33.3419V3.586C33.3419 7.366 36.2039 10.552 39.1469 11.551ZM41.6039 20.65C41.6039 18.733 40.0379 17.491 37.8509 17.491C35.6909 17.491 34.1249 18.733 34.1249 20.65C34.1249 22.567 35.6909 23.836 37.8509 23.836C40.0379 23.836 41.6039 22.567 41.6039 20.65ZM44.6009 20.65C44.6009 24.106 41.7929 26.401 37.8509 26.401C33.9359 26.401 31.1549 24.106 31.1549 20.65C31.1549 17.437 33.9359 14.926 37.8509 14.926C41.7929 14.926 44.6009 17.437 44.6009 20.65ZM48.5699 9.013H44.4929V14.71H41.4959V0.669999H44.4929V6.448H48.5699V9.013ZM52.6448 9.202C52.6448 11.713 54.0218 13.171 55.7768 13.171C57.6128 13.171 58.9358 11.713 58.9358 9.202C58.9358 6.718 57.6128 5.206 55.7768 5.206C54.0218 5.206 52.6448 6.718 52.6448 9.202ZM68.3858 23.242V0.723999H71.3288V23.242H68.3858ZM49.7828 9.229C49.7828 5.206 52.2938 2.56 55.7768 2.56C58.4768 2.56 60.4748 4.045 61.3928 6.502C61.5278 6.907 61.6358 7.312 61.7168 7.744H63.5798V1.048H66.4418V22.378H63.5798V10.309H61.7978C61.7168 10.903 61.5818 11.47 61.3928 11.983C60.4748 14.386 58.5038 15.898 55.7768 15.898C52.2938 15.898 49.7828 13.252 49.7828 9.229ZM95.7961 4.747H82.9441V2.182H95.7961V4.747ZM103.842 25.483H88.9111V17.248H91.9081V22.891H103.842V25.483ZM103.437 18.409H100.44V11.875H96.5521V9.283H100.44V7.204H96.5521V4.639H100.44V0.696999H103.437V18.409ZM96.7141 12.361L96.9301 14.818C92.8801 15.385 87.1561 15.601 82.5391 15.466L82.3231 12.901C83.2681 12.928 84.3211 12.928 85.4281 12.928L84.8881 6.178L87.7771 5.962L88.1551 12.928C89.0731 12.928 89.9371 12.901 90.8011 12.874L91.2601 5.962L94.0951 6.151L93.5011 12.766C93.5281 12.739 93.5821 12.739 93.6091 12.739C94.7971 12.658 95.8231 12.55 96.7141 12.361ZM122.74 14.035L123.064 16.546C119.446 17.383 113.02 17.545 108.538 17.329V8.149H116.935V4.558H108.484V1.966H119.932V10.687H111.535V14.818C114.397 14.953 119.635 14.683 122.74 14.035ZM125.008 23.215V0.723999H127.978V23.215H125.008ZM146.174 6.988H131.81V4.477H146.174V6.988ZM142.583 3.127H135.698V0.588999H142.583V3.127ZM141.881 12.28C141.881 11.065 140.828 10.201 139.208 10.201C137.561 10.201 136.508 11.065 136.508 12.28C136.508 13.441 137.561 14.359 139.208 14.359C140.828 14.359 141.881 13.441 141.881 12.28ZM144.77 12.28C144.77 14.98 142.259 16.681 139.208 16.681C136.049 16.681 133.511 14.98 133.511 12.28C133.511 9.445 136.049 7.825 139.208 7.825C142.34 7.825 144.77 9.445 144.77 12.28ZM148.415 23.782V20.461H139.91V23.782H148.415ZM151.385 26.347H136.94V17.869H151.385V26.347ZM155.462 9.904H151.385V16.519H148.415V0.669999H151.385V7.339H155.462V9.904ZM171.849 5.827C171.849 4.261 170.229 3.046 168.015 3.046C165.72 3.046 164.181 4.207 164.181 5.827C164.181 7.528 165.666 8.635 168.015 8.635C170.229 8.635 171.849 7.528 171.849 5.827ZM174.9 5.827C174.9 9.31 171.444 11.119 168.042 11.119C164.613 11.119 161.103 9.364 161.103 5.827C161.103 2.533 164.37 0.507998 168.015 0.507998C171.822 0.507998 174.9 2.587 174.9 5.827ZM176.223 26.482H160.482V19.948H172.605V18.409H160.482V15.898H175.548V22.405H163.398V23.971H176.223V26.482ZM179.382 14.413H156.648V11.848H179.382V14.413ZM1.457 54.843V40.128H13.175V42.693H4.454V52.143C7.316 52.278 10.934 52.062 14.093 51.387L14.39 54.033C10.556 54.816 6.452 55.032 1.457 54.843ZM21.383 61.215H18.386V48.228H12.068V45.69H18.386V38.724H21.383V61.215ZM39.2549 44.61H24.8909V42.207H39.2549V44.61ZM35.6639 41.127H28.7789V38.697H35.6639V41.127ZM34.9619 49.389C34.9619 48.444 33.9089 47.715 32.2889 47.715C30.6419 47.715 29.5889 48.444 29.5889 49.389C29.5889 50.334 30.6419 51.063 32.2889 51.063C33.9089 51.063 34.9619 50.334 34.9619 49.389ZM37.8509 49.362C37.8509 51.792 35.3399 53.304 32.2889 53.304C29.1299 53.304 26.5919 51.792 26.5919 49.362C26.5919 47.04 29.1299 45.447 32.2889 45.447C35.4209 45.447 37.8509 47.04 37.8509 49.362ZM44.9249 64.455H30.2369V58.11H41.4959V56.652H30.2369V54.222H44.4659V60.486H33.2339V62.025H44.9249V64.455ZM48.5429 47.607H44.4659V52.953H41.4959V38.67H44.4659V45.042H48.5429V47.607ZM78.5992 61.215H75.5752V48.606H71.1472V46.014H75.5752V38.724H78.5992V61.215ZM59.3482 40.047H70.5532C70.6342 48.714 66.3142 53.547 59.2132 56.274L57.6742 53.898C60.7792 52.791 63.1012 51.387 64.7212 49.551C65.1802 49.038 65.5582 48.498 65.9092 47.931L58.7812 48.417L58.2412 45.798L66.9622 45.447C67.2052 44.583 67.3942 43.638 67.4752 42.639H59.3482V40.047ZM91.9081 51.792V42.693H86.2381V51.792H91.9081ZM94.9321 54.33H83.2141V40.128H94.9321V54.33ZM103.464 61.215H100.44V48.282H96.2551V45.744H100.44V38.724H103.464V61.215ZM116.611 39.75H120.04V41.127C120.04 45.555 123.82 48.417 128.032 49.578L126.655 52.17C122.686 50.982 119.743 48.714 118.339 45.798C117.043 48.687 113.857 51.063 110.05 52.224L108.646 49.632C112.912 48.471 116.611 45.555 116.611 41.127V39.75ZM129.706 58.515H106.972V56.004H129.706V58.515ZM158.871 38.994V41.559H142.59V38.994H158.871ZM162.111 52.656H139.377V50.091H162.111V52.656ZM159.168 46.23V48.687H142.32V46.23H145.695L145.263 42.801L148.341 42.558L148.665 46.23H152.823L153.228 42.558L156.36 42.801L155.766 46.23H159.168ZM158.952 64.374H143.022V58.029H155.361V56.652H143.022V54.141H158.277V60.459H145.938V61.89H158.952V64.374ZM179.2 54.465V55.329C179.2 58.92 183.007 61.35 186.679 61.917L185.356 64.374C181.792 63.51 178.876 61.701 177.607 59.19C176.284 61.755 173.557 63.537 169.804 64.482L168.616 62.025C172.558 61.323 175.96 59.028 175.96 55.329V54.465H179.2ZM175.933 49.578L176.23 52.143C173.152 52.71 168.67 52.872 164.782 52.656V44.934H171.532V42.396H164.782V39.858H174.529V47.418H167.779V50.172C170.263 50.307 173.692 50.064 175.933 49.578ZM185.815 54.951H182.845V47.013H180.307V52.98H177.391V39.021H180.307V44.448H182.845V38.67H185.815V54.951ZM205.199 61.836V58.326H195.641V61.836H205.199ZM208.547 39.264V41.775H192.266V39.264H208.547ZM208.844 47.067V49.524H201.905V51.603H211.787V54.141H189.053V51.603H198.962V49.524H191.996V47.067H195.398L194.939 43.044L198.044 42.774L198.368 47.067H202.472L202.904 42.774L206.036 43.044L205.442 47.067H208.844ZM208.169 64.347H192.671V55.761H208.169V64.347Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
